import mustache from 'mustache';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { LOCAL_STORAGE } from '@/constants';
import { logger } from '@/services/logger';

import { useIndexedDB } from './useIndexedDB';

interface Translation {
    [key: string]: string;
}

interface CachedTranslationObject {
    languageKey: string,
    map: {
        languageCode: string;
        translations: Translation;
    }
}

interface TranslationContextInterface {
    t: (key: string,
        interpolationValues?: Record<string, string | number>,
        interpolationType?: 'messageformat') => string;
}

const TranslationContext = createContext<TranslationContextInterface | undefined>(undefined);

// Characters that must stay escaped when a translation is parsed as HTML
// (toasts, confirmation dialogs). Slash, backtick and "=" are omitted: Mustache
// encodes them as entities, and React text (for example the browse validation
// tooltip) then shows those entities literally.
const HTML_TEMPLATE_ESCAPE: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

function escapeForHtmlTemplate(value: unknown) {
    return String(value).replace(/[&<>"']/g, (character) => HTML_TEMPLATE_ESCAPE[character]);
}

// Validation messages use single-brace placeholders and are rendered as React text.
// They must not be HTML-escaped, or "/" becomes "&#x2F;" and "'" becomes "&#39;".
function interpolateTranslation(
    translation: string,
    interpolationValues?: Record<string, string | number>,
    interpolationType?: 'messageformat',
) {
    if (interpolationType === 'messageformat') {
        return mustache.render(translation, interpolationValues, {}, {
            tags: ['{', '}'],
            escape: (value) => String(value),
        });
    }

    return mustache.render(translation, interpolationValues, {}, {
        escape: escapeForHtmlTemplate,
    });
}

/**
 * A somewhat dumb translation hook that mimics react-i18next API that grabs
 * the translations from the legacy app using localStorage and indexed db.
 *
 * This hook would be updated in the final processes of the migration.
 */
const useTranslation = () => {
    const translationsContext = useContext(TranslationContext);

    if (!translationsContext) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }

    return translationsContext;
};

function TranslationProvider({ children }: PropsWithChildren<Record<never, never>>) {

    const [translations, setTranslations] = useState<Translation>({});
    const [loading, setLoading] = useState(true);
    const { get: getCachedTranslations } = useIndexedDB<CachedTranslationObject>('translations');

    useEffect(() => {

        const fetchTranslations = async () => {
            try {
                const languageKey = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);

                if (languageKey) {
                    const cachedTranslations = await getCachedTranslations(languageKey);
                    if (cachedTranslations && cachedTranslations.map.translations) {
                        logger.debug('TranslationProvider: Successfully loaded cached translations');
                        setTranslations(cachedTranslations.map.translations);
                    }
                } else {
                    logger.error(`TranslationProvider: Language key was not found in local storage ${languageKey}`);
                }

                setLoading(false);
            } catch (error) {
                logger.error('TranslationProvider: Error fetching translations from IndexedDB:', error);
                setLoading(false);
            }
        };

        fetchTranslations();

    }, []);

    const t = useCallback((key: string, interpolationValues?: Record<string, string | number>, interpolationType?: 'messageformat') => {
        //Handle undefined keys to prevent mustache render errors
        if (!key) {
            return '';
        }

        const translation = translations[key] || key;

        // To retain backward compatibility with Angular-Translate, we have to support the 'messageformat' used
        // to translate validation messages.
        // See more: https://angular-translate.github.io/docs/#/guide/14_pluralization:~:text=Overriding%20interpolation%20temporarily
        try {
            return interpolateTranslation(translation, interpolationValues, interpolationType);
        } catch (error) {
            logger.warn(`Failed to parse translation template for key "${key}":`, error);
            return translation;
        }
    }, [translations]);

    if (loading) {
        return <Loader />;
    }

    return <TranslationContext.Provider value={{ t }}>{children}</TranslationContext.Provider>;
};

export { interpolateTranslation, TranslationContext, TranslationProvider, useTranslation };
